import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  fetchDepartment,
  fetchJob,
  fetchBusinessDomain,
  fetchMarketMembersByRolesAndIndustry,
  assignMarketMemberToDepartment,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function BuildDepartmentMemberMoveMarketPage() {
  const { businessDomainId, businessUnitId, departmentId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);

  const memberListPath = `/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/members`;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. 部門からジョブIDを取得
      const dept = await fetchDepartment(departmentId);
      if (!dept.jobId) { setRows([]); return; }

      // 2. ジョブからロール一覧を取得
      const job = await fetchJob(dept.jobId);
      const roles = Array.isArray(job.roles) ? job.roles : [];

      // 3. 事業ドメインからindustryIdを取得
      const domain = await fetchBusinessDomain(businessDomainId);
      if (!domain.industryId) { setRows([]); return; }

      // 4. ロールと業種で市場メンバーを取得
      const data = await fetchMarketMembersByRolesAndIndustry(domain.industryId, roles);
      setRows(
        (Array.isArray(data) ? data : []).map((m) => ({
          ...m,
          fullName: m.jaName || m.enName || `${m.lastName || ""} ${m.firstName || ""}`.trim(),
        }))
      );
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, businessDomainId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleMove = async (member) => {
    setMoving(true);
    try {
      // fullName はUI用の付加フィールドのため除外して送信（旧アプリと同じデータ構造）
      // eslint-disable-next-line no-unused-vars
      const { fullName, ...memberData } = member;
      const res = await assignMarketMemberToDepartment(departmentId, COMPANY_ID, memberData);
      if (res?.result !== "OK") {
        throw new Error(res?.errorMessage || "assignment_failed");
      }
      notify.success(t("message.move.success"));
      navigate(memberListPath, { replace: true });
    } catch (e) { notify.apiError(e); }
    finally { setMoving(false); }
  };

  const columns = [
    { field: "fullName", headerName: t("member.name"), flex: 1, minWidth: 160 },
    { field: "personName", headerName: t("member.personName"), flex: 1, minWidth: 160 },
    {
      field: "_actions",
      headerName: "",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          disabled={moving}
          onClick={(e) => { e.stopPropagation(); handleMove(params.row); }}
        >
          {t("member.move")}
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("build.department.member.move.market.title")}</Typography>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.memberId}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        sx={{ "& .MuiDataGrid-row": { cursor: "default" } }}
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/members`)}>{t("common.back")}</Button>
      </Box>
    </Box>
  );
}
